// supervisor-startup-before-dispatch-result.ts
//
// Split out of supervisor-mission-run-startup.ts (that file was at the
// 200-line budget and about to grow further for O-2 forensics work — plan
// 20260924-1400 follow-up). Owns the §18 STARTUP before-dispatch block: the
// journal write AND (B-1 fix, dogfood recheck 260925) building the full
// `RunMissionResult` so a block hit at startup carries the SAME
// `resumeAt`/`beforeDispatchCode` fields a mid-run block already does
// (supervisor-before-dispatch-gate-step.ts's mapBeforeDispatchGateVerdictTo
// Result). Before this fix, `supervisor-mission-run.ts` built the result
// inline with only `outcome`/`ticksRun`/`beforeDispatchReason` — dropping
// `start.beforeDispatch.resumeAt`/`.code` even though `evaluateBeforeDispatch`
// (supervisor-dispatch-guards.ts) always returns them on `max_sessions_per_hour`/
// `quiet_hours`. Measured: `avatar autopilot run` re-invoked while a session
// cap was still in effect printed "Xử lý lý do trên rồi chạy lại" with NO
// resume time, forcing an operator/scheduler to guess when to retry.
import { randomUUID } from "node:crypto";
import { MissionStore } from "./mission-store.js";
import { isTerminalBeforeDispatchCode } from "./supervisor-before-dispatch-gate-step.js";
import type { BeforeDispatchDecision } from "./supervisor-dispatch-guards.js";
import type { RunMissionResult } from "./supervisor-mission-run-types.js";

/**
 * Pure: maps a blocked `BeforeDispatchDecision` (startSupervisor's ONE-TIME
 * startup gate evaluation) onto `RunMissionResult` — same field shape the
 * mid-run gate path already produces, so a `--json` consumer never has to
 * special-case "blocked at startup" vs "blocked mid-run".
 */
export function buildStartupBeforeDispatchBlockedResult(
  decision: BeforeDispatchDecision,
): RunMissionResult {
  // Re-review 2026-09-26 (A-F1): a ceiling breach is terminal here too —
  // same `blocked` + code/reason shape the mid-run gate step produces, so a
  // scheduler never reads "chạy lại sau" on a mission a human must unblock.
  if (isTerminalBeforeDispatchCode(decision.code)) {
    return {
      outcome: "blocked",
      ticksRun: 0,
      beforeDispatchReason: decision.reason,
      beforeDispatchCode: decision.code,
    };
  }
  return {
    outcome: "before_dispatch_blocked",
    ticksRun: 0,
    beforeDispatchReason: decision.reason,
    beforeDispatchCode: decision.code,
    resumeAt: decision.resumeAt,
  };
}

/**
 * §18 before-dispatch block journal, moved out of supervisor-mission-run.ts
 * (was inlined there) so that file stays under its 200-line budget. Same
 * commit shape as before — `blocked:<code>` — just relocated.
 */
export async function journalStartupBeforeDispatchBlocked(
  controlRoot: string,
  code: string | undefined,
): Promise<void> {
  const store = await MissionStore.open(controlRoot);
  try {
    await store.commit([
      {
        event_id: randomUUID(),
        delta: { kind: "mission_status_changed", status: `blocked:${code ?? "before_dispatch"}` },
      },
    ]);
  } finally {
    await store.close();
  }
}
